import BaseController from "./base_controller"
import { t } from "@/utils/i18n"

// Connects to data-controller="modal-entry"
// Controller attached to dialog element within global turbo_frame_tag "entry_form"
export default class ModalEntryController extends BaseController {
  private static opener: HTMLElement | null = null
  private boundHandleCloseEvent: ((event: CustomEvent) => void) | null = null
  private boundFrameLoadHandler: ((event: Event) => void) | null = null
  private frameObserver: MutationObserver | null = null

  connect(): void {
    super.connect()
    
    // Listen for custom events from turbo streams
    this.boundHandleCloseEvent = this.handleCloseEvent.bind(this)
    document.addEventListener('modal-entry:close', this.boundHandleCloseEvent as EventListener)

    this.boundFrameLoadHandler = this.handleFrameLoad.bind(this)
    document.addEventListener('turbo:frame-load', this.boundFrameLoadHandler)

    this.startFrameObserver()
    this.ensureModalVisible()
  }

  disconnect(): void {
    super.disconnect()
    
    // Clean up event listeners
    if (this.boundHandleCloseEvent) {
      document.removeEventListener('modal-entry:close', this.boundHandleCloseEvent as EventListener)
    }
    if (this.boundFrameLoadHandler) {
      document.removeEventListener('turbo:frame-load', this.boundFrameLoadHandler)
    }
    this.stopFrameObserver()
  }

  async openNewEntry(event: MouseEvent): Promise<void> {
    event.preventDefault()
    
    try {
      // Get date from button if available (for calendar integration)
      const target = event.currentTarget as HTMLElement
      ModalEntryController.opener = target
      const date = target.dataset.date
      
      // Use entry navigation controller for locale-aware URL
      const navigationController = this.application.getControllerForElementAndIdentifier(
        document.body, 'entry-navigation'
      ) as any
      
      let url: string
      if (navigationController) {
        url = date ? `${navigationController.newEntryUrlValue}?date=${date}` : navigationController.newEntryUrlValue
      } else {
        // Fallback to hardcoded URL if navigation controller not found
        url = date ? `/entries/new?date=${date}` : '/entries/new'
      }
      
      // Use Turbo Stream instead of custom fetch
      const form = this.createTurboForm(url, 'GET', {}, 'entry_form')
      
      // Submit the form to get Turbo Stream response
      this.submitTurboForm(form)
      
    } catch (error) {
      this.handleError(error, 'openNewEntry')
      this.showErrorMessage(t('error_load_new_entry_modal'))
    }
  }

  async openEditEntry(event: MouseEvent): Promise<void> {
    event.preventDefault()
    
    try {
      const target = event.currentTarget as HTMLElement
      ModalEntryController.opener = target
      const entryId = target.dataset.entryId
      if (!entryId) {
        throw new Error('Entry ID not found')
      }
      
      // Use entry navigation controller for locale-aware URL
      const navigationController = this.application.getControllerForElementAndIdentifier(
        document.body, 'entry-navigation'
      ) as any
      
      const url = navigationController ? 
        navigationController.editEntryUrlValue.replace(':id', entryId) : 
        `/entries/${entryId}/edit`
      
      // Use Turbo Stream instead of custom fetch
      const form = this.createTurboForm(url, 'GET', {}, 'entry_form')
      
      // Submit the form to get Turbo Stream response
      this.submitTurboForm(form)
      
    } catch (error) {
      this.handleError(error, 'openEditEntry')
      this.showErrorMessage(t('error_load_edit_entry_modal'))
    }
  }

  closeModal(event?: Event): void {
    if (event) {
      event.preventDefault()
    }

    try {
      // Close the dialog element
      const modal = document.getElementById('entry_modal') as HTMLDialogElement | null
      if (modal) {
        if (typeof modal.close === 'function') {
          modal.close()
        } else {
          modal.removeAttribute('open')
        }
      }

      // Clear the global turbo frame to remove modal content
      const frame = document.getElementById('entry_form')
      if (frame) {
        frame.innerHTML = ''
      }
      this.toggleModalBodyState(false)
      if (ModalEntryController.opener?.isConnected) ModalEntryController.opener.focus()
      ModalEntryController.opener = null

      // Trigger modal close event for any listeners
      this.dispatch('closed')

    } catch (error) {
      this.handleError(error, 'closeModal')
    }
  }

  // Handle successful form submission (called from turbo stream)
  handleSuccessEvent(event: CustomEvent): void {
    try {
      // Close modal
      this.closeModal()
      
      // Show success message if provided
      const { message } = event.detail
      if (message) {
        this.showSuccessMessage(message)
      }
      
    } catch (error) {
      this.handleError(error, 'handleSuccessEvent')
    }
  }

  // Handle close event from turbo stream
  private handleCloseEvent(event: CustomEvent): void {
    try {
      // Close modal
      this.closeModal()
      
      // Show success message if provided
      const { message } = event.detail
      if (message) {
        this.showSuccessMessage(message)
      }
      
    } catch (error) {
      this.handleError(error, 'handleCloseEvent')
    }
  }

  private toggleModalBodyState(isOpen: boolean): void {
    try {
      const targets = [document.documentElement, document.body]
      targets.forEach((el) => {
        if (!el || !el.classList) return
        if (isOpen) {
          el.classList.add('modal-open')
        } else {
          el.classList.remove('modal-open')
        }
      })
    } catch (error) {
      this.handleError(error, 'toggleModalBodyState')
    }
  }

  private handleFrameLoad(event: Event): void {
    try {
      const target = event.target as HTMLElement
      if (target?.id !== 'entry_form') return
      const modal = target.querySelector<HTMLDialogElement>('#entry_modal')
      if (modal) {
        if (typeof modal.showModal === 'function' && !modal.open) {
          modal.showModal()
        } else {
          modal.setAttribute('open', '')
        }
        this.toggleModalBodyState(true)
      }
    } catch (error) {
      this.handleError(error, 'handleFrameLoad')
    }
  }

  private startFrameObserver(): void {
    try {
      const frame = document.getElementById('entry_form')
      if (!frame) return

      this.frameObserver = new MutationObserver(() => this.ensureModalVisible())
      this.frameObserver.observe(frame, { childList: true, subtree: true })
    } catch (error) {
      this.handleError(error, 'startFrameObserver')
    }
  }

  private stopFrameObserver(): void {
    if (this.frameObserver) {
      this.frameObserver.disconnect()
      this.frameObserver = null
    }
  }

  private ensureModalVisible(): void {
    try {
      const frame = document.getElementById('entry_form')
      if (!frame) return
      const modal = frame.querySelector<HTMLDialogElement>('#entry_modal')
      if (!modal) return

      if (typeof modal.showModal === 'function' && !modal.open) {
        modal.showModal()
      } else {
        modal.setAttribute('open', '')
      }
      this.toggleModalBodyState(true)
      this.forceVisibleTestControls(modal)
    } catch (error) {
      this.handleError(error, 'ensureModalVisible')
    }
  }

  private forceVisibleTestControls(modal: HTMLDialogElement): void {
    try {
      if (!this.isSystemTestMode()) return

      this.unlockModalLayout(modal)
      this.ensureDoseSectionVisible(modal)

      const makeInteractive = (el: HTMLElement) => {
        el.style.position = 'static'
        el.style.opacity = '1'
        el.style.clip = 'auto'
        el.style.width = 'auto'
        el.style.height = 'auto'
        el.style.pointerEvents = 'auto'
      }

      modal.querySelectorAll<HTMLInputElement>('.rating input').forEach((input) => {
        makeInteractive(input)
        input.removeAttribute('tabindex')
      })

      modal.querySelectorAll<HTMLElement>('.toggle').forEach((toggle) => {
        makeInteractive(toggle)
        toggle.removeAttribute('tabindex')
      })
    } catch (error) {
      this.handleError(error, 'forceVisibleTestControls')
    }
  }

  private isSystemTestMode(): boolean {
    return document.cookie?.includes('system_test_mode=1') || false
  }

  private unlockModalLayout(modal: HTMLDialogElement): void {
    if (!modal) return
    modal.style.opacity = '1'
    modal.style.visibility = 'visible'
    modal.style.pointerEvents = 'auto'
    modal.style.position = 'static'
    modal.style.transform = 'none'

    const modalBox = modal.querySelector<HTMLElement>('.modal-box')
    if (modalBox) {
      modalBox.style.position = 'static'
      modalBox.style.transform = 'none'
      modalBox.style.maxHeight = 'none'
    }

    const backdrop = modal.querySelector<HTMLElement>('.modal-backdrop')
    if (backdrop) {
      backdrop.style.display = 'none'
    }
  }

  private ensureDoseSectionVisible(modal: HTMLDialogElement): void {
    const doseContent = modal?.querySelector<HTMLElement>('#dose-content')
    if (doseContent) {
      doseContent.classList.remove('hidden')
      doseContent.style.removeProperty('display')
    }
  }
}

